import type { TFunction } from 'i18next';
import type { Initiative } from '../../api/initiative-client/initiative-client.type';
import { formatDate, formatMonthDate } from '../../helpers/date-time-helpers';
import { Metric } from './strategy.type';

/**
 * Formats an initiative date string to a human-readable format
 *
 * @param {string | undefined | null} date - The date string to format
 * @param {TFunction} t - Translation function from `useTranslation()` so the
 *   "no date" fallback respects org-level text_config overrides
 * @returns {string} The formatted date string
 */
const formatInitiativeDate = (date: string | undefined | null, t: TFunction): string => {
  return formatDate(date, { fallback: t('strategy.initiatives.no_date', '-') });
};

/**
 * Returns a human-readable label for a given metric
 *
 * @param {Metric} metric - The metric to get a label for
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The human-readable label for the metric
 */
const getMetricLabel = (metric: Metric, t: TFunction): string => {
  switch (metric) {
    case Metric.Tasks:
      return t('strategy.initiatives.metrics.tasks', { work_units: t('common.work_units') });
    case Metric.Points:
      return t('strategy.initiatives.metrics.points', { estimation: t('common.estimation') });
    default:
      return metric;
  }
};

/**
 * Formats a date string to a human-readable format
 *
 * @param {string} date - The date string to format
 * @returns {string} The formatted date string
 */
const formatDateLabel = (date: string): string => {
  return formatMonthDate(date);
};

/**
 * Filters initiatives by a search term, matching against key or name
 *
 * @param {Initiative[]} initiatives - The array of initiatives to filter
 * @param {string} searchTerm - The search term to match against
 * @returns {Initiative[]} Filtered array of initiatives matching the search term
 */
const filterInitiativesBySearchTerm = (initiatives: Initiative[], searchTerm: string): Initiative[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return initiatives;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return initiatives.filter((initiative) => {
    const keyMatch = initiative.key && initiative.key.toLowerCase().includes(normalizedSearchTerm);
    const nameMatch = initiative.name && initiative.name.toLowerCase().includes(normalizedSearchTerm);

    return keyMatch || nameMatch;
  });
};

export { filterInitiativesBySearchTerm, formatDateLabel, formatInitiativeDate, getMetricLabel };
